// src/utils/proposalLockedCopy.js
//
// Locked copy transcribed from docs/proposal/FIG_Proposal_Template_v1.md —
// §3.2 (Service Team), §3.3 (Services), §3.7 (Coverage disclaimer),
// §3.8 (Service Fees), §3.10 (Acceptance disclaimer) and §1.6's footer string.
//
// Every string here is byte-exact from that file. The prose in the source is
// hard-wrapped inside markdown blockquotes; the wrapping is presentational, so
// continuation lines are joined with a single space and nothing else is altered.
// Em dashes (U+2014) are preserved. There are no curly apostrophes to preserve:
// the source uses straight ASCII apostrophes throughout ("Lloyd's", "can't",
// "who've", "industry's").
//
// This module is data only. It reads nothing, draws nothing, imports nothing,
// and must never throw at import time. Hash verification deliberately does NOT
// live here — it is in proposalLockedCopy.test.js. Proposals are generated by
// the `generate_proposal` tool on sharepoint-mcp (see its docs/plays/INDEX.md);
// this app only uses the footer string, via proposalLayout, for the Loss Runs PDF.
//
// EMPHASIS: a block's text is EITHER a plain string (no emphasis anywhere) or
// an array of runs — { text, bold?, italic? }. Only the strings that actually
// carry emphasis use the run form; everything else stays a plain string. The
// markdown ** and * markers are formatting, not content, so they are parsed out
// rather than transcribed: printing them literally would put asterisks in the
// PDF.
//
// The prose renderer needs a drawRuns pass to consume run arrays — measuring
// each run in its own face and advancing the pen across them within a wrapped
// line. THAT DOES NOT EXIST YET. This is the data side only. runsToText below
// is a flattening fallback, not a substitute for it.

/**
 * sha256 of docs/proposal/FIG_Proposal_Template_v1.md at transcription time.
 *
 * DRIFT GUARD. If the vault file changes, this hash stops matching and
 * proposalLockedCopy.test.js reports it — the signal that the copy below needs
 * re-transcribing. A plain string constant is bundler-safe; the hashing that
 * consumes it is not, which is why it lives in the test.
 */
export const SOURCE_SHA256 =
  '7ddc846271bc7d0a3cb5cf786b07222f102014c7d578d89486c30f3df84e6174';
// Re-pinned 2026-09-21 after §3.9's Fees + Taxes rows became one "Taxes and
// Fees" line. §3.9 is not locked copy; every locked section (§3.2, §3.3, §3.7,
// §3.8, §3.10) was byte-identical across that edit, so nothing below changed.

/**
 * Flatten a plain string or a run array to plain text.
 *
 * Fallback for measurement and for paths that cannot yet render emphasis. It
 * loses the bold/italic information by design — it is NOT the drawRuns pass.
 *
 * @param {string|Array<{text: string}>} textOrRuns
 * @returns {string}
 */
export function runsToText(textOrRuns) {
  if (typeof textOrRuns === 'string') return textOrRuns;
  if (!Array.isArray(textOrRuns)) return '';
  return textOrRuns.map((r) => (r && typeof r.text === 'string' ? r.text : '')).join('');
}

/**
 * §3.2 Service Team — LOCKED ROSTER, in table order.
 *
 * §3.2 declares this roster canonical: "This roster is now canonical; changes
 * are deliberate edits to this file, not per-proposal typing." Do not edit an
 * entry here to fix a one-off proposal — change the vault file, re-transcribe,
 * and update SOURCE_SHA256.
 *
 * §3.2 also carries a correction notice: prior proposals rendered
 * `marc@myfiginurance.com`, missing the `s`. The corrected address is taken
 * from the roster table as it now stands. See the regression test that pins it.
 *
 * All four appear on every proposal (§3.2).
 */
export const SERVICE_TEAM = [
  {
    name: 'Marc Fragos',
    role: 'Agency Principal',
    phone: '(615) 593-4544',
    email: 'marc@myfiginsurance.com',
  },
  {
    name: 'Angela Fragos',
    role: 'Commercial Account Manager',
    phone: '(615) 482-1485',
    email: 'angela@myfiginsurance.com',
  },
  {
    name: 'Alan de la Houssaye',
    role: 'Wholesale Broker, CRC Group',
    phone: '(713) 329-8939',
    email: 'adelahoussaye@crcgroup.com',
  },
  {
    name: 'Maggie Beckley',
    role: 'Employee Benefits Manager',
    phone: '(832) 214-3025',
    email: 'maggie.beckley@benefitmall.com',
  },
];

/** §3.3's header line, kept separate from the body blocks. */
export const SERVICES_HEADER = 'Fragos Insurance Group, LLC — Services';

/**
 * §3.3 Services — LOCKED COPY, as bodyBlocks-shaped entries: plain strings for
 * paragraphs, { heading, text } where §3.3 carries a subhead.
 *
 * Block shapes: a plain string, `{ heading, text? }`, `{ bullet: true, runs }`,
 * or a bare run array. A bare run array is emphasised prose that is NOT a list
 * item; only `bullet: true` marks a list item.
 *
 * The three bullets under "What Sets FIG Apart" carry runs because they carry
 * emphasis in the source: each opens with a `**bold lead**` and the second
 * contains `*you*`. The markers are parsed out; the runs' concatenated text is
 * byte-exact against the source with markers stripped, which a test pins by
 * re-deriving from the file.
 *
 * The bullets are separate entries deliberately, because they need
 * hanging-indent treatment — a bullet glyph in the margin with the wrapped
 * text aligned to the text edge, not to the bullet. The prose renderer has no
 * hanging indent yet, so laying these out will need more than drawParagraph.
 */
export const SERVICES_BLOCKS = [
  'FIG is a Texas-based specialty agency dealing exclusively in complex commercial property and casualty exposures. Our expertise is hard-to-place risk — placements that require the Excess & Surplus (E&S) markets, Lloyd\'s syndicates, and specialty programs where standard carriers can\'t or won\'t play. Manufacturing, contracting, transportation, environmental, layered property towers, and other complex structures are where we live.',
  'We maintain direct admitted-market appointments, deep wholesale relationships with firms like CRC Group and First Choice/MarshBerry, and are multi-state licensed.',
  {
    heading: 'Group Captives',
    text: 'For clients who\'ve outgrown guaranteed-cost insurance, we structure, place, and administer member-owned captive programs end-to-end, turnkey. A major differentiator versus traditional agencies.',
  },
  {
    heading: 'Employee Benefits',
    text: 'We work with the industry\'s best benefit brokers and captive markets, and stay involved at every step — plan design, enrollment, claims, and service.',
  },
  // Heading-only entry: §3.3 gives this subhead no paragraph of its own, just
  // the three bullets that follow. `text` is omitted rather than set to ''.
  { heading: 'What Sets FIG Apart' },
  // bullet: true comes from the markdown list marker ('- ') on these three
  // lines in §3.3 — the only place that fact is still visible. Renderers must
  // key hanging indent off this flag, never off "the block happens to be a run
  // array": that only held while the bullets were the sole run arrays.
  {
    bullet: true,
    runs: [
      { text: 'Zero captive agreements. Zero carrier quotas.', bold: true },
      { text: ' We are independent by design. What we recommend serves your account — never a production quota, never a carrier loyalty requirement.' },
    ],
  },
  {
    bullet: true,
    runs: [
      { text: 'Seasoned counsel, by choice.', bold: true },
      { text: ' You work directly with a senior commercial professional — someone ' },
      { text: 'you', italic: true },
      { text: ' choose, who gets your work done. No ticket queues, no tiered service pools.' },
    ],
  },
  {
    bullet: true,
    runs: [
      { text: 'Technology leaders, not followers.', bold: true },
      { text: ' FIG is a major innovator in back-office insurance technology. We set the trend rather than chase it — which means faster certificates, faster endorsements, cleaner documentation, better visibility into your program.' },
    ],
  },
  'FIG is in the business of serving you — keeping you covered.',
];

/** §3.7 Coverage disclaimer — LOCKED COPY. Header in §3.7 is "Coverage". */
export const COVERAGE_DISCLAIMER =
  'This presentation is designed to give the insured an overview of the insurance coverage recommended for your company. The proposal should not be construed as a legal interpretation of the insurance policies represented. Please review directly your insurance contracts for details regarding coverages, conditions, and exclusions. It is CRITICAL any changes in locations, company name, new entities or additional insureds, products, contracts or leases, vehicle changes, acquisitions or disposal of property of any kind or vacancy of any insured building be communicated with our firm as soon as possible.';

/**
 * §3.8 Service Fees — LOCKED STATUTORY COPY — DO NOT EDIT.
 *
 * This is the Texas statutory service-fee disclosure, citing Sections 550.001,
 * 4005.003 and 4005.004 of the Texas Insurance Code. Per §3.8 it "renders on
 * **every** proposal regardless of whether an agency fee is charged" — there is
 * no conditional path, and none should be added.
 *
 * "Changes to this text require legal review, not a schema edit." That applies
 * equally here: this is not a code edit. Do not reword, re-punctuate or
 * "tidy" these two paragraphs. See the note in the report about the grammar in
 * the second one, which is reproduced as written on purpose.
 */
export const SERVICE_FEES_BLOCKS = [
  'Our agency may provide certain services that you request or that are necessary to place or maintain your insurance. Sections 550.001, 4005.003 and 4005.004 of the Texas Insurance Code authorize us to charge a fee for services if we obtain your written consent prior to providing the service or incurring the expense. The fee may be charged in addition to any commission we may receive from the insurance company providing the insurance coverage. In some cases where we charge a fee and also receive compensation from an insurer or other third party, we are required to disclose this fact to you. The purpose of this Disclosure Statement is to comply with these requirements. You agree to pay the amount proposed as compensation for services and or expenses incurred. We may propose and charge you an agent fee in addition to or in place of commission.',
  'We may also receive additional compensation, bonuses, commissions, profit sharing based on premium and or profitability from the insurer and is not guaranteed.',
];

/**
 * §3.10 Acceptance — the DISCLAIMER paragraph, as runs with a bold label.
 *
 * NOTE: unlike §3.3's bullets, the source carries NO emphasis markers here —
 * §3.10 is inside a fenced code block, so `DISCLAIMER:` is plain text in the
 * file. The bold lead is an editorial addition made on instruction, not
 * something derived from the markdown. The label was split off
 * programmatically, so the prose after it is still byte-exact.
 */
export const ACCEPTANCE_DISCLAIMER = [
  { text: 'DISCLAIMER:', bold: true },
  { text: ' Information contained in this proposal is intended to provide you with a brief overview of the coverages and is provided for reference purposes only. The precise coverage afforded is subject to the terms, conditions, and exclusions of the policies issued and the information you have provided. Any requests that you make to confirm, bind, or alter your insurance program through e-mail, voice mail, or other automated systems will not take effect until you receive confirming communication from your agent.' },
];

/**
 * §1.6's left-hand footer text. THE single definition — proposalLayout.js
 * imports this rather than keeping its own copy, so the string cannot drift.
 */
export const FOOTER_DISCLAIMER =
  'This is only a brief summary, not a contract. Please see full policy details for limitation and exclusions.';

// Hash verification lives in proposalLockedCopy.test.js. It is not exported
// from here and must not be reintroduced: it needs node:crypto, which cannot be
// imported from a src/ module without either breaking a CRA production build or
// resorting to eval to hide the dependency from webpack. The check has no
// runtime job anyway.
